import React from "react";
import { Link } from "react-router-dom";
import CcgRoutePremiumPage from "../components/CcgRoutePremiumPage";
import PostcodeQuotenew from "./PostcodeQuotenew";
import "./CcgRoutePremium.css";

const cardGrid = (className, cards) => (
  <div className={className}>
    {cards.map((card) => (
      <article className={card.className || ""} key={card.title}>
        <h3>{card.title}</h3>
        {card.body ? <p>{card.body}</p> : null}
        {card.items ? <ul>{card.items.map((item) => <li key={item}>{item}</li>)}</ul> : null}
        {card.link ? <Link to={card.link.to} className={card.link.className || "route-link-inline"}>{card.link.label}</Link> : null}
        {card.links ? (
          <div className="route-link-btn-row">
            {card.links.map((link) => <Link to={link.to} className={link.className || "route-link-btn"} key={link.to}>{link.label}</Link>)}
          </div>
        ) : null}
      </article>
    ))}
  </div>
);

const officialLinks = [
  { href: "https://www.hrr.co.uk/venue-timings/", label: "Venue timings" },
  { href: "https://www.hrr.co.uk/travel-parking/", label: "Travel and parking" },
  { href: "https://www.hrr.co.uk/racing-schedule/", label: "Racing schedule" },
  { href: "https://www.prestigevenuesandevents.sodexo.com/henley-royal-regatta", label: "Official hospitality" },
];

const henleyTaxiRoute = {
  id: "henley-taxi",
  path: "/henley-taxi",
  liveStripVariant: "maidenhead",
  seo: {
    title: "Henley Taxi - Henley Royal Regatta, Town & Heathrow Transfers | CheapCabGrab",
    description:
      "Book Henley taxis for Henley-on-Thames, Henley Royal Regatta 2026, town centre drop-offs, hotels, Heathrow transfers and group travel. Fixed fares and licensed drivers.",
    keywords:
      "Henley taxi, Henley Royal Regatta taxi, Henley-on-Thames taxi, Henley to Heathrow taxi, Heathrow to Henley taxi, Henley Regatta transfer, CheapCabGrab",
    canonical: "https://www.cheapcabgrab.com/henley-taxi",
    ogTitle: "Henley Taxi - Henley Royal Regatta & Heathrow Transfers",
    ogDescription:
      "Fixed-price Henley taxis for Henley Royal Regatta 2026, Henley-on-Thames town centre, hotels, stations and Heathrow Airport.",
    serviceName: "CheapCabGrab - Henley Taxi Service",
    areaServed: ["Henley-on-Thames", "Henley Royal Regatta", "Heathrow Airport", "Marlow", "Reading", "Maidenhead"],
    offerCatalogName: "Henley Taxi Routes",
    offerName: "Henley fixed-price taxi service",
    offerDescription: "Fixed-price taxi transfers for Henley-on-Thames, Henley Royal Regatta 2026, Heathrow Airport and nearby Thames Valley towns.",
    reservationName: "Taxi booking in Henley-on-Thames",
  },
  hero: {
    eyebrow: "Henley taxi and Regatta transfers",
    h1: "Henley Taxi - Henley Royal Regatta, Town & Heathrow Transfers",
    subtitle: "Henley-on-Thames, Regatta town centre drop-offs, hotels, station pickups and Heathrow Airport.",
    body:
      "Book Henley taxis with fixed upfront fares, licensed drivers and secure online booking. Ideal for Henley Royal Regatta 2026, town centre drop-offs, hotel pickups, Henley station connections and Heathrow transfers.",
  },
  quote: {
    label: "Get instant Henley taxi quotes",
    note: "Free - Fast - No obligation",
    presetPickup: "Henley-on-Thames",
    presetDropoff: "",
  },
  flags: [
    "Henley Royal Regatta 2026 taxi planning",
    "Town centre and A4130 drop-off guidance",
    "Fixed fares agreed upfront - no meters",
    "Saloon, MPV, minibus and coach options",
  ],
  reviewPageKey: "/henley-taxi",
  reviewAriaLabel: "Henley taxi customer reviews",
  lhrStatusTitle: "Live Heathrow checks for Henley airport taxis",
  floatingCta: "Book Henley Taxi",
  mobileCta: "Book Henley Taxi",
  supportNudge: "Need Henley taxi help?",
  sections: [
    {
      id: "henley-regatta-2026",
      title: "Henley Royal Regatta 2026 taxi planning",
      body:
        "Henley Royal Regatta 2026 is scheduled over six racing days from Tuesday 30 June to Sunday 5 July. Henley town centre, the riverbank, station approaches and the A4130 can be busy, so pre-booking is strongly recommended.",
      render: () => cardGrid("route-overview-grid", [
        {
          title: "Key Regatta dates",
          items: [
            "Qualifying races: Friday 26 June 2026",
            "The Draw: Saturday 27 June 2026",
            "Racing days: Tuesday 30 June to Sunday 5 July 2026",
            "Prizegiving: Sunday 5 July from 17:00",
          ],
        },
        {
          title: "Racing and enclosure timings",
          items: [
            "Tuesday racing starts 09:30; enclosures open 09:00",
            "Wednesday to Saturday racing starts 09:00; enclosures open 08:30",
            "Sunday racing starts 10:50; enclosures open 10:00",
            "Most racing days finish by 19:30; Sunday last race is expected at 16:30",
          ],
        },
        {
          title: "Taxi use cases",
          items: [
            "Town centre taxi drop-offs for the short walk to the Regatta site",
            "Henley station to hotel or riverbank connections",
            "Evening returns after racing, hospitality and restaurants",
            "MPVs for picnic items, formalwear, luggage and family groups",
          ],
        },
      ]),
    },
    {
      id: "henley-official-links",
      title: "Official Henley Royal Regatta links",
      body: "Use official Henley Royal Regatta pages for event timing, travel, parking and hospitality information. CheapCabGrab handles taxi planning and booking.",
      render: () => (
        <div className="route-links-grid">
          {officialLinks.map((link) => (
            <article key={link.href}>
              <h3>{link.label}</h3>
              <p>Open the official Henley Royal Regatta information before confirming event day plans.</p>
              <a href={link.href} className="route-link-btn" target="_blank" rel="noreferrer">
                Open official HRR page
              </a>
            </article>
          ))}
        </div>
      ),
    },
    {
      id: "henley-travel",
      title: "Henley Regatta travel and drop-off notes",
      body: "The Regatta site is on the Berkshire bank within walking distance of Henley town and train station. Traffic controls and event flow can affect where vehicles can wait.",
      render: () => cardGrid("route-areas-grid", [
        {
          title: "Taxi drop-off guidance",
          items: [
            "Town centre drop-off is usually the most convenient option",
            "Main Regatta taxi drop-off is on the A4130, around a five-minute walk from the enclosures",
            "Henley-on-Thames station is around walking distance from the town and Regatta route",
            "Allow extra time around racing start, lunch intervals and evening departures",
          ],
          link: { to: "/henley-to-heathrow", label: "Henley to Heathrow route ->" },
        },
        {
          title: "Driving and parking context",
          items: [
            "Henley is around 13 miles from M4 Junction 8/9",
            "Henley is around 16 miles from M40 Junction 4",
            "Official car parks use RG9 2LP and event signage symbols",
            "Car parks open daily from 07:30 and gates are locked at 21:00",
          ],
        },
      ]),
    },
    {
      id: "henley-airport-routes",
      title: "Henley, Heathrow and group travel",
      body: "Use the Henley taxi hub for local Regatta and town travel, or the dedicated airport route page for Heathrow transfers.",
      render: () => cardGrid("route-links-grid", [
        {
          title: "Henley to Heathrow",
          body: "Fixed-price airport taxi from Henley-on-Thames, Regatta hotels, town centre and station pickups to Heathrow terminals.",
          link: { to: "/henley-to-heathrow", label: "Open Henley to Heathrow", className: "route-link-btn" },
        },
        {
          title: "Heathrow to Henley",
          body: "Arriving for the Regatta or a Thames Valley hotel stay? Use the quote form with Heathrow Airport as pickup and Henley-on-Thames as drop-off.",
          link: { to: "/heathrow-taxi", label: "Heathrow taxi transfers", className: "route-link-btn" },
        },
        {
          title: "MPV, minibus and coach",
          body: "Regatta groups often need extra luggage space, coordinated pickup and planned return times after racing or hospitality.",
          links: [
            { to: "/minibus", label: "Minibus" },
            { to: "/coach", label: "Coach", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "henley-nearby-routes",
      title: "Nearby taxi routes from Henley",
      body: "Henley visitors often connect through Maidenhead, Windsor, Reading and Marlow hotels before or after Regatta week.",
      render: () => cardGrid("route-links-grid", [
        {
          title: "Maidenhead to Heathrow",
          body: "Useful for groups staying along the Thames Valley with a direct M4 corridor airport transfer.",
          link: { to: "/maidenhead-to-heathrow", label: "Open Maidenhead route", className: "route-link-btn" },
        },
        {
          title: "Windsor taxis",
          body: "Useful for Windsor Castle stays, hotels and visitor groups combining Windsor and Henley.",
          link: { to: "/windsor-taxi", label: "Open Windsor taxi", className: "route-link-btn" },
        },
        {
          title: "Legoland and Thorpe Park",
          body: "Family groups can compare nearby attraction planning before booking wider Thames Valley travel.",
          links: [
            { to: "/legoland-budget-planner", label: "LEGOLAND planner" },
            { to: "/thorpepark-budget-planner", label: "Thorpe Park planner", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "henley-faq",
      title: "Henley taxi - FAQs",
      body: "Common questions for Henley-on-Thames, Henley Royal Regatta 2026 and Heathrow taxi bookings.",
      render: () => (
        <div className="route-faq-grid">
          {[
            ["Should I pre-book a taxi for Henley Royal Regatta?", "Yes. Regatta week creates high demand around Henley town centre, hotels, station approaches and evening return times."],
            ["Where is the Regatta taxi drop-off?", "The main taxi drop-off during Regatta is on the A4130, a short walk from the enclosures. Town centre drop-offs are also useful depending on timing and road controls."],
            ["Can you take us from Heathrow to Henley?", "Yes. Heathrow to Henley-on-Thames is a common airport transfer for Regatta guests, hotel stays and Thames Valley visitors."],
            ["Do you offer MPVs or minibuses for Henley?", "Yes. MPVs, minibuses and coach options are available for larger Regatta groups, luggage-heavy airport journeys and coordinated hotel pickups."],
          ].map(([title, body]) => <article className="route-faq-card" key={title}><h3>{title}</h3><p>{body}</p></article>)}
        </div>
      ),
    },
    {
      id: "henley-support",
      title: "Need help with a Henley taxi booking?",
      body: "For Regatta groups, hotel pickups, Heathrow Airport transfers or return timing after racing, use support before booking.",
      render: () => (
        <div className="route-support-box">
          <p>Email: <a href="mailto:support@cheapcabgrab.com">support@cheapcabgrab.com</a></p>
          <p>
            For airport travel, use our <Link to="/henley-to-heathrow">Henley to Heathrow</Link> route or compare nearby{" "}
            <Link to="/maidenhead-to-heathrow">Maidenhead to Heathrow</Link>,{" "}
            <Link to="/windsor-to-heathrow">Windsor to Heathrow</Link> and{" "}
            <Link to="/taplow-to-heathrow">Taplow to Heathrow</Link> pages.
          </p>
        </div>
      ),
    },
  ],
};

const HenleyTaxi = () => (
  <CcgRoutePremiumPage route={henleyTaxiRoute} QuoteComponent={PostcodeQuotenew} />
);

export default HenleyTaxi;
